import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import {
  clearAuthRedirectDestination,
  DEFAULT_AUTH_DESTINATION,
  oauthErrorMessage,
  readAuthRedirectDestination,
  sanitizeAuthDestination,
  storeAuthRedirectDestination,
} from '../../lib/authRedirect';

interface GoogleSignInProps {
  onBackToHome: () => void;
  redirectTo?: string;
}

export const SIGN_IN_RECOVERY_TIMEOUT_MS = 8_000;

const GoogleSignIn: React.FC<GoogleSignInProps> = ({ onBackToHome, redirectTo }) => {
  const { signInWithGoogle } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const recoveryTimerRef = useRef<number | null>(null);
  const callbackError = oauthErrorMessage(location.search, location.hash);
  const requestedDestination = useMemo(() => {
    const queryDestination = new URLSearchParams(location.search).get('next');
    return (
      sanitizeAuthDestination(redirectTo) ??
      sanitizeAuthDestination(queryDestination)
    );
  }, [location.search, redirectTo]);

  const clearRecoveryTimer = useCallback(() => {
    if (recoveryTimerRef.current !== null) {
      window.clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
  }, []);

  const startGoogleSignIn = useCallback(async () => {
    clearRecoveryTimer();
    setLoading(true);
    setError(null);
    storeAuthRedirectDestination(
      readAuthRedirectDestination() ?? DEFAULT_AUTH_DESTINATION
    );

    recoveryTimerRef.current = window.setTimeout(() => {
      recoveryTimerRef.current = null;
      setLoading(false);
      setError(
        'Google sign-in did not finish. You can retry or return home.'
      );
    }, SIGN_IN_RECOVERY_TIMEOUT_MS);

    try {
      const result = await signInWithGoogle();
      if (result.error) {
        clearRecoveryTimer();
        setError(result.error.message || 'Failed to sign in with Google');
        setLoading(false);
      }
      // On success, keep the bounded recovery timer active until navigation.
    } catch (err: any) {
      clearRecoveryTimer();
      setError(err?.message || 'An unexpected error occurred');
      setLoading(false);
    }
  }, [clearRecoveryTimer, signInWithGoogle]);

  // Automatically redirect to Google OAuth when component loads
  useEffect(() => {
    const existingDestination = readAuthRedirectDestination();
    storeAuthRedirectDestination(
      requestedDestination ?? existingDestination ?? DEFAULT_AUTH_DESTINATION
    );

    if (callbackError) {
      setError(callbackError);
      setLoading(false);
      return clearRecoveryTimer;
    }

    void startGoogleSignIn();
    return clearRecoveryTimer;
  }, [
    callbackError,
    clearRecoveryTimer,
    requestedDestination,
    startGoogleSignIn,
  ]);

  const handleBackToHome = () => {
    clearRecoveryTimer();
    clearAuthRedirectDestination();
    onBackToHome();
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Back button */}
        <Button
          onClick={handleBackToHome}
          variant="ghost"
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors mb-6 p-0 justify-start"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Home</span>
        </Button>

        <div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            {loading ? 'Redirecting to Google...' : 'Sign in to TopClip'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {loading ? 'Please wait while we redirect you to Google Sign In' : 'Create viral clips with AI-powered editing'}
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <Button
            onClick={() => void startGoogleSignIn()}
            disabled={loading}
            variant="outline"
            className="group relative w-full flex justify-center py-3 px-4 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            <div className="flex items-center space-x-3">
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              <span>
                {loading ? 'Redirecting...' : 'Continue with Google'}
              </span>
            </div>
          </Button>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              By signing in, you agree to our{' '}
              <Link
                to="/terms"
                className="text-gray-700 hover:text-gray-900 underline"
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                to="/privacy"
                className="text-gray-700 hover:text-gray-900 underline"
              >
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleSignIn;
